import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArrowLeft,
    FaEnvelope,
    FaEye,
    FaSearch,
} from "react-icons/fa";
import FullPageSpinner from "../../../components/FullPageSpinner";
import Contact from "../../../models/Contact";
import { sendRequest } from "../../../utils/api";
import { timeAgo } from "../../../utils/date";
import { initAllStores } from "../../../utils/cache";


export default function ContactLogs() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const [contacts, setContacts] = useState<Contact[]>([] as Contact[]);
    const [loading, setLoading] = useState(false);



    useEffect(() => {
        const getContacts = async () => {

            const cache = await initAllStores();

            setLoading(true);
            try {
                const contacts = await sendRequest("GET", "/contacts");

                await cache.clear("contacts");
                await cache.saveMany({ "contacts": contacts });

                setContacts(contacts);

            } catch (err: any) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        }
        getContacts();
    }, []);

    const [page, setPage] = useState(1);

    // Replace with values from your backend
    const pagination = {
        page,
        totalPages: 1,
        hasNextPage: page < 0,
        hasPreviousPage: page > 1,
    };


    const filtered =
        contacts.filter((contact) =>
            `${contact.name} ${contact.subject} ${contact.email}`
                .toLowerCase()
                .includes(search.toLowerCase())
        );

    const badge = (isRead: boolean) =>
        !isRead
            ? "bg-red-100 text-red-700"
            : "bg-green-100 text-green-700";

    const statusText = (isRead: boolean) =>
        isRead ? "Read" : "Unread";


    return (
        <div className="p-6">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

                <FullPageSpinner show={loading} />

                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/expenses")}
                        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                    >
                        {FaArrowLeft({
                            size: 13,
                            className: "",
                        })}
                    </button>

                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">
                            Contact Logs
                        </h1>

                        <p className="mt-1 text-xs text-slate-400">
                            Messages received from your website.
                        </p>
                    </div>
                </div>

                <div className="relative w-full md:w-80">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" >{FaSearch({})}</span>

                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search messages..."
                        className="w-full rounded-lg border py-3 pl-11 pr-4 outline-none focus:border-blue-500"
                    />
                </div>
            </div>

            <div className="overflow-hidden rounded-xl border bg-white shadow-sm">

                {/* Desktop Table */}
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-600">
                                <th className="px-6 py-4">Sender</th>
                                <th className="px-6 py-4">Subject</th>
                                <th className="px-6 py-4">Received</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4 text-center">Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length > 0 ? (
                                filtered.map((contact) => (
                                    <tr
                                        key={contact.id}
                                        className="border-t hover:bg-gray-50"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                                    {FaEnvelope({})}
                                                </div>

                                                <div>
                                                    <p className="font-medium text-gray-900">
                                                        {contact.name}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        {contact.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4">
                                            <p className="font-medium text-gray-700">
                                                {contact.subject}
                                            </p>
                                        </td>

                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {timeAgo(contact.createdAt)}
                                        </td>

                                        <td className="px-6 py-4">
                                            <span
                                                className={`rounded-full px-3 py-1 text-xs font-semibold ${badge(
                                                    contact.isRead
                                                )}`}
                                            >
                                                {statusText(contact.isRead)}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={() =>
                                                    navigate(`/contacts/${contact.id}`)
                                                }
                                                className="inline-flex items-center justify-center rounded-lg p-2 text-blue-600 transition hover:bg-blue-50"
                                                title="View contact"
                                            >
                                                {FaEye({})}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                                                <span className="text-xl text-gray-400">
                                                    {FaEnvelope({})}
                                                </span>
                                            </div>

                                            <h3 className="text-lg font-semibold text-gray-700">
                                                No contacts found
                                            </h3>

                                            <p className="mt-1 text-sm text-gray-500">
                                                {search
                                                    ? "No contacts match your search."
                                                    : "You don't have any contact messages yet."}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div className="divide-y md:hidden">
                    <div className="divide-y md:hidden">
                        {filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                                    <span className="text-xl text-gray-400">
                                        {FaEnvelope({})}
                                    </span>
                                </div>

                                <h3 className="text-lg font-semibold text-gray-700">
                                    No contacts found
                                </h3>

                                <p className="mt-1 text-sm text-gray-500">
                                    {search
                                        ? "No contacts match your search."
                                        : "You don't have any contact messages yet."}
                                </p>
                            </div>
                        ) : (
                            filtered.map((contact) => (
                                <div
                                    key={contact.id}
                                    className="p-4"
                                >
                                    <div className="flex justify-between">
                                        <div className="flex gap-3">

                                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                                                <span className="text-blue-600" >{FaEnvelope({})}</span>
                                            </div>

                                            <div>
                                                <h3 className="font-semibold">
                                                    {contact.name}
                                                </h3>

                                                <p className="text-sm text-gray-500">
                                                    {contact.email}
                                                </p>
                                            </div>

                                        </div>

                                        <button
                                            onClick={() =>
                                                navigate(`/contacts/${contact.id}`)
                                            }
                                            className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                                        >
                                            {FaEye({})}
                                        </button>
                                    </div>

                                    <div className="mt-4 space-y-2 text-sm">

                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Subject</span>
                                            <span className="font-medium">
                                                {contact.subject}
                                            </span>
                                        </div>

                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Received</span>
                                            <span>{timeAgo(contact.createdAt)}</span>
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500">Status</span>

                                            <span
                                                className={`rounded-full px-3 py-1 text-xs font-semibold ${badge(
                                                    contact.isRead
                                                )}`}
                                            >
                                                {statusText(contact.isRead)}
                                            </span>
                                        </div>

                                    </div>
                                </div>
                            ))
                        )}
                    </div>



                </div>

            </div>

            {filtered.length > 0 && <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4">
                <button
                    disabled={!pagination.hasPreviousPage}
                    onClick={() => setPage((prev) => prev - 1)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${pagination.hasPreviousPage
                        ? "bg-white hover:bg-gray-100"
                        : "cursor-not-allowed bg-gray-100 text-gray-400"
                        }`}
                >
                    Previous
                </button>

                <span className="text-sm text-gray-600">
                    Page <span className="font-semibold">{pagination.page}</span> of{" "}
                    <span className="font-semibold">{pagination.totalPages}</span>
                </span>

                <button
                    disabled={!pagination.hasNextPage}
                    onClick={() => setPage((prev) => prev + 1)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${pagination.hasNextPage
                        ? "bg-white hover:bg-gray-100"
                        : "cursor-not-allowed bg-gray-100 text-gray-400"
                        }`}
                >
                    Next
                </button>
            </div>}
        </div>
    );
}